import { DOCUMENT, registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import {
  ApplicationConfig,
  LOCALE_ID,
  provideZoneChangeDetection,
  APP_INITIALIZER,
  inject,
  importProvidersFrom
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';

import { NgxEchartsModule } from 'ngx-echarts';
import { firstValueFrom } from 'rxjs';

import { MessageService } from 'primeng/api';

import { routes } from '../../app.routes';
import { provideCore } from './core.config';
import { provideIcons } from './icons.config';
import { providePrimeNGTheme } from './primeng.config';
import { AuthFacade } from '../../modules/auth/facades';
import { environment } from '../../../environments/environment';
import { getDomainRedirect, LANDING_PATHS } from './domain-routing';

registerLocaleData(localePt, 'pt-BR');

/**
 * Restore private sessions before routing; public landing pages need no session.
 */
function initializeAuth(): () => Promise<boolean> {
  const authFacade = inject(AuthFacade);
  const location = inject(DOCUMENT).location;
  const redirect = getDomainRedirect(new URL(location.href), environment.appUrl, environment.landingUrl);
  if (redirect && redirect.origin !== location.origin) {
    return () => {
      location.replace(redirect.href);
      return Promise.resolve(false);
    };
  }
  const pathname = location.pathname.replace(/\/+$/, '') || '/';
  const isLandingPage = LANDING_PATHS.includes(pathname) && !redirect;
  if (isLandingPage) {
    return () => Promise.resolve(false);
  }
  return () => firstValueFrom(authFacade.initializeFromStorage());
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideRouter(routes),
    providePrimeNGTheme(),
    provideCore(),
    provideIcons(),
    MessageService,
    {
      provide: LOCALE_ID,
      useValue: 'pt-BR'
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      multi: true
    },
    importProvidersFrom(
      NgxEchartsModule.forRoot({
        echarts: () => import('echarts')
      })
    )
  ]
};
